import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { pagePath } from '../lib/pages'

interface SearchHit {
  kind: 'customer' | 'site' | 'device'
  id: string
  label: string
  sub: string
  customer_name: string
  customer_slug: string
  site_name?: string
}

/**
 * Globale Suche in der Kopfzeile. Sie bleibt kundenübergreifend, zeigt aber zu
 * jedem Treffer Kunde und Standort – und findet nur, was der angemeldete
 * Benutzer auch sehen darf (docs/design.md).
 */
export function GlobalSearch() {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 200)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const { data: hits } = useQuery({
    queryKey: ['search', debounced],
    queryFn: ({ signal }) =>
      apiGet<SearchHit[]>(`/search?q=${encodeURIComponent(debounced)}`, signal),
    enabled: debounced.length >= 2,
    staleTime: 10_000,
  })

  function go(hit: SearchHit) {
    setOpen(false)
    setQuery('')
    switch (hit.kind) {
      case 'customer':
        navigate({ to: pagePath('', hit.customer_slug) })
        break
      case 'site':
        navigate({ to: pagePath('standorte', hit.customer_slug) })
        break
      case 'device':
        navigate({ to: `${pagePath('geraete', hit.customer_slug)}/${hit.id}` })
        break
    }
  }

  const labels: Record<SearchHit['kind'], string> = {
    customer: 'Kunde',
    site: 'Standort',
    device: 'Gerät',
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', flexGrow: 1, maxWidth: 420 }}>
      <label className="sr-only" htmlFor="globale-suche">
        Globale Suche
      </label>
      <input
        id="globale-suche"
        className="input"
        type="search"
        placeholder="Gerät, IP, MAC oder Kunde"
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
      />

      {open && debounced.length >= 2 ? (
        <div
          className="card"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            maxHeight: 400,
            overflow: 'auto',
            zIndex: 40,
            boxShadow: '0 12px 32px var(--scrim)',
          }}
        >
          {(hits ?? []).length === 0 ? (
            <div className="empty" style={{ padding: 20 }}>
              <span>Keine Treffer.</span>
            </div>
          ) : (
            (hits ?? []).map((hit) => (
              <button
                key={`${hit.kind}-${hit.id}`}
                type="button"
                className="nav-link"
                style={{
                  width: '100%',
                  height: 'auto',
                  padding: '10px 14px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onClick={() => go(hit)}
              >
                <span className="stack" style={{ gap: 2 }}>
                  <span style={{ color: 'var(--text)', fontWeight: 600 }}>{hit.label}</span>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {labels[hit.kind]} · {hit.customer_name}
                    {hit.site_name ? ` · ${hit.site_name}` : ''}
                    {hit.sub ? ` · ${hit.sub}` : ''}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  )
}
